import { Typography } from '@mui/material'
import {
  Box,
  DatePicker,
  LabraToolTip,
  TextInput,
} from '@labrav/react-components'

import { InfoOutlined } from '@mui/icons-material'
import { useStyles } from './DetailsGCP.styles'
import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { getRandom } from '../../../../../common/utils/helperFunctions'
import { Product } from '../../../../../modules/productsListing/reducer'
import { FormikProps, useFormikContext } from 'formik'
import { PrivateOffer } from '../../../../../modules/privateOffer/reducer'
import { RecipientsGCP } from './Recipients/RecipientsGCP'
import { OfferStakeHolders } from './OfferStakeHolders/OfferStakeHolders'
import { get } from 'lodash'
import { dateMonthYear } from '../../../../../common/utils/constants'

export type DetailsGCPProp = {
  product: Product
  externalBaseURL?: string
}

export const DetailsGCP = React.memo(({ externalBaseURL }: DetailsGCPProp) => {
  const { values, errors, touched, handleBlur, setFieldValue, handleChange } =
    useFormikContext() as FormikProps<PrivateOffer>
  const { classes } = useStyles()
  const [randomKey, setRandomKey] = useState<number>(getRandom())
  const offerRecipients = values.offerRecipients || []
  useEffect(() => {
    setRandomKey(getRandom())
  }, [offerRecipients.length])

  const field: FormField = {
    title: 'Date',
    humanReadableKey: 'Subscription end date',
    placeHolder: 'Subscription end date',
    id: 'offerExpirationDate',
    isRequired: false,
    isReadOnly: false,
    isCustom: false,
    defaultValue: values.offerExpirationDate,
    dataType: 'date',
    minDate: 'today',
  }

  const handleDateChange = useCallback((date: string | null) => {
    setFieldValue('offerExpirationDate', date)
  }, [])

  return (
    <div>
      <Box heading={'Details'} subHeading={''}>
        <div
          className={clsx(
            classes.subHeadingContainer,
            classes.companyDetailSubHead
          )}
        >
          <Typography
            className={classes.subHeading}
            data-testid="details-subheading-gcp"
          >
            Company details
          </Typography>
          <LabraToolTip
            tooltipPlacement={'right'}
            label={
              'Enter details of the company who is intended to receive and accept the offer.'
            }
          >
            <InfoOutlined className={classes.infoIcon} />
          </LabraToolTip>
        </div>
        <div className={classes.companyDetailRow}>
          <div className={classes.name} key={`companyName-${randomKey}`}>
            <TextInput
              isRequired={false}
              keyName="companyName"
              title="Company name"
              value={values.companyName}
              onChange={handleChange}
              isReadOnly={false}
              type="string"
              placeHolder=""
              variant="regularV2"
              onBlur={handleBlur}
              isError={
                !!(
                  get(touched, 'companyName', false) &&
                  get(errors, 'companyName', false)
                )
              }
              errorText={
                (!!get(touched, 'companyName', false) &&
                  get(errors, 'companyName', ' ')) ||
                ' '
              }
            />
          </div>
          <div
            className={classes.awsAccountId}
            key={`awsAccountId-${randomKey}`}
          >
            <TextInput
              placeHolder=""
              isRequired={false}
              isReadOnly={false}
              keyName="buyerCloudBillingId"
              onChange={handleChange}
              type="string"
              onBlur={handleBlur}
              title="GCP billing account ID"
              isError={
                !!(
                  get(touched, 'buyerCloudBillingId', false) &&
                  get(errors, 'buyerCloudBillingId', false)
                )
              }
              errorText={
                (!!get(touched, 'buyerCloudBillingId', false) &&
                  get(errors, 'buyerCloudBillingId', ' ')) ||
                ' '
              }
              value={values.buyerCloudBillingId as string}
              variant="regularV2"
            />
          </div>
        </div>
        {/* recipients */}
        <RecipientsGCP />

        {/* email message */}
        <div
          className={classes.customEmailMessage}
          key={`customMessage-${randomKey}`}
        >
          <TextInput
            placeHolder=""
            isRequired={false}
            isReadOnly={false}
            keyName="emailCustomMessage"
            onChange={handleChange}
            type="string"
            title={
              <>
                Custom email message
                <span className={classes.requiredAsterisk}>*</span>
              </>
            }
            onBlur={handleBlur}
            isError={
              !!(
                get(touched, 'emailCustomMessage', false) &&
                get(errors, 'emailCustomMessage', false)
              )
            }
            errorText={
              (!!get(touched, 'emailCustomMessage', false) &&
                get(errors, 'emailCustomMessage', ' ')) ||
              ' '
            }
            value={values.emailCustomMessage as string}
            variant="regularV2"
          />
        </div>

        <OfferStakeHolders externalBaseURL={externalBaseURL} />

        <div
          className={clsx(
            classes.subHeadingContainer,
            classes.companyDetailSubHead
          )}
        >
          <Typography
            className={classes.subHeading}
            data-testid="details-subheading"
          >
            Notes for your team (Optional)
          </Typography>
          <LabraToolTip
            tooltipPlacement={'right'}
            label={
              'It is an optional field where you can add any internal note related to the offer.'
            }
          >
            <InfoOutlined className={classes.infoIcon} />
          </LabraToolTip>
        </div>

        <div className={classes.notes} key={`notes-${randomKey}`}>
          <TextInput
            placeHolder=""
            isRequired={false}
            isReadOnly={false}
            keyName="notes"
            onChange={handleChange}
            type="string"
            title="Notes for your team"
            onBlur={handleBlur}
            isError={
              !!(get(touched, 'notes', false) && get(errors, 'notes', false))
            }
            errorText={
              (!!get(touched, 'notes', false) && get(errors, 'notes', ' ')) ||
              ' '
            }
            value={values.notes || ''}
            variant="regularV2"
          />
        </div>

        <div className={classes.offerDetailsRow}>
          <div>
            <div className={classes.subHeadingContainer}>
              <Typography
                className={classes.subHeading}
                data-testid="details-subheading"
              >
                Offer details
              </Typography>
              <LabraToolTip
                tooltipPlacement={'right'}
                label={'Name of the private offer.'}
              >
                <InfoOutlined className={classes.infoIcon} />
              </LabraToolTip>
            </div>
            <div
              className={classes.offerName}
              key={`privateOfferName-${randomKey}`}
            >
              <TextInput
                placeHolder=""
                isRequired={false}
                isReadOnly={false}
                keyName="privateOfferName"
                onChange={handleChange}
                type="string"
                title="Offer name"
                onBlur={handleBlur}
                isError={
                  !!(
                    get(touched, 'privateOfferName', false) &&
                    get(errors, 'privateOfferName', false)
                  )
                }
                errorText={
                  (!!get(touched, 'privateOfferName', false) &&
                    get(errors, 'privateOfferName', ' ')) ||
                  ' '
                }
                value={values.privateOfferName as string}
                variant="regularV2"
              />
            </div>
          </div>
          <div>
            <div className={classes.subHeadingContainer}>
              <Typography
                className={classes.subHeading}
                data-testid="acceptBy-date-heading"
              >
                Offer expiration date
              </Typography>
              <LabraToolTip
                tooltipPlacement={'right'}
                label={
                  'Date on which the offer will expire. Buyer needs to accept the offer before this date. Offer expires at the end of the day on the specified date.'
                }
              >
                <InfoOutlined className={classes.infoIcon} />
              </LabraToolTip>
            </div>
            <div className={classes.datePicker}>
              <DatePicker
                format={dateMonthYear}
                field={field}
                key={'offerExpirationDate'}
                errorHelperText={''}
                handleDateChange={handleDateChange}
                customClasses={classes.textField}
              />
            </div>
          </div>
        </div>
      </Box>
    </div>
  )
})
